import type { SocialLink } from '@/types/socialLinks';
import type { SponsorListItem } from '@/types/sponsor.types';
import type { TeamRegistrationField } from '@/types/tournament/game';

export enum TeamProfileTab {
  SOCIAL = 'social',
  SPONSORS = 'sponsors',
}

/** Options for `teamService.getTeamById` (invite gate + optional sponsor embed). */
export type GetTeamByIdOptions = {
  inviteId?: string;
  /** When true, response embeds team sponsors (detail page only). */
  includeSponsors?: boolean;
};

export enum TeamMemberRole {
  PLAYERS = 'players',
  SUBSTITUTES = 'substitutes',
  MANAGER = 'manager',
  COACH = 'coach',
}

export enum CaptainReassignmentFlowType {
  MOVE_TO_SUBSTITUTES = 'move-to-substitutes',
  REMOVE_MEMBER = 'remove-member',
  SWAP = 'swap',
}

export enum TeamInviteModalMode {
  JOIN = 'join',
  EDIT = 'edit',
}

export enum TeamMemberCardLoadingType {
  MOVING = 'moving',
  SWAPPING = 'swapping',
  REMOVING = 'removing',
  MAKING_CAPTAIN = 'making-captain',
  EDITING_DETAILS = 'editing-details',
}

export interface CountryCode {
  name: string;
  code: string;
  abbreviation: string;
  number: string | number;
}

export interface ProfilePicture {
  url: string;
  name: string;
  size: number;
  type: string;
}

export interface DiscordProfile {
  id: string;
  discordUsername: string;
  name: string;
  verified: boolean;
  email: string;
  profilePicture: ProfilePicture | null;
}

export interface MobileNo {
  code: string;
  number: string;
  name?: string | null;
  abbreviation?: string | null;
}

export interface TeamMember {
  _id: string;
  email: string;
  username?: string;
  name: string;
  profilePicture?: ProfilePicture;
  isDeleted: boolean;
  isUserBanned: boolean;
  countryCode: CountryCode;
  kycVerified: boolean;
  den: string | null;
  verificationStatus: 'VERIFIED' | 'NOT_VERIFIED' | 'VERIFICATION_REQUESTED';
  createdAt: string;
  updatedAt: string;
  __v: number;
  discordProfile: DiscordProfile;
  emailVerificationResendCount: number;
  mobileNo: MobileNo;
  inGameName: string;
  inGameId: string;
  inDiscordId: string;
  teamId: string;
  role: 'players' | 'substitutes' | 'coach' | 'manager';
  tournamentId: string | null;
  customFields: Record<string, unknown>[];
  id: string;
  is18AndAbove?: boolean;
}

export interface TeamMembers {
  players: TeamMember[];
  substitutes: TeamMember[];
  coach: TeamMember[];
  manager: TeamMember[];
  captain: TeamMember;
}

export interface ClanDetails {
  _id: string;
  name: string;
  /** Optional clan slug when surfaced by the eligible-teams API; preferred over `_id` when building `/clan-profile/:clanIdentifier` links. Mirrors `orgSlug` naming. */
  clanSlug?: string | null;
  clanVariant: number;
  logo: ProfilePicture;
  bio: string;
  members: {
    creator?: string;
    owner: string[];
    admin: string[];
  };
  verificationStatus: string;
  isDeleted: boolean;
  isActive: boolean;
  coverImage: ProfilePicture;
  kycVerified: boolean;
  aka: string;
  isVerified: boolean;
  den: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  notifications: {
    noAdminOwnerNotificationsCount: number;
  };
}

export interface GameDetails {
  _id: string;
  name: string;
  logo: ProfilePicture;
  bio: string;
  maxPlayers: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  platforms: string[];
  pointsPerKill: number;
  minPlayers: number;
  isGameEnabled: boolean;
  isActive: boolean;
  isBettingEnabled: boolean;
  isEntryFeeEnabled: boolean;
  isPrizePoolEnabled: boolean;
  participantsAllowedPerTeam: number[];
  regions: string[];
  pointSystem: Record<string, number>;
  participantType: string;
  activeBetLogo: ProfilePicture;
  disabledBetLogo: ProfilePicture;
  maxPlayersPerTeam: number;
  teamRegistrationFields?: TeamRegistrationField[];
}

export interface Team {
  _id: string;
  clanId: string;
  gameId: string;
  name: string;
  teamSlug?: string;
  teamSlugUpdatedAt?: string;
  logo?: ProfilePicture;
  members: TeamMembers;
  isDeleted: boolean;
  teamCreatedBy: string;
  isClanLogo: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  tournament: number;
  clanDetails: ClanDetails;
  gameDetails: GameDetails;
  /**
   * Team-level country code (ISO/abbreviation), e.g. "IN".
   */
  country?: string;
  socialLinks?: SocialLink[];
  sponsors?: SponsorListItem[];
}

export interface UpdateTeamRequest {
  members: {
    players: string[];
    substitutes: string[];
    coach: string[];
    manager: string[];
    captain: string | null;
  };
}

export interface AddToTeamRequest {
  name?: string;
  email?: string;
  inGameName?: string;
  inGameId?: string;
  role: string;
  mobileNo?: MobileNo;
}

export interface TeamResponse {
  query: string;
  result: Team;
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}

export interface TeamsResponse {
  query: string;
  result: Team[];
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}
